// src/components/ui/Sidebar.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LayoutDashboard, Building2, Building, Users, Briefcase, Activity, FileText, Settings, GitMerge, DollarSign, Calendar, WifiOff, Loader2, Clock, X, Shield, ClipboardList, ChevronDown, User, Bell, Tag, ScrollText, FileStack, ClipboardCheck } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSidebar } from '../../contexts/SidebarContext';
import { useCompany } from '../../contexts/CompanyContext';
import { supabase } from '../../lib/supabase';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile } = useAuth();
  const { isOpen, close } = useSidebar();
  const { effectiveAccountType } = useCompany();
  const [connectionStatus, setConnectionStatus] = useState('checking'); // 'online', 'offline', 'checking'
  const [expandedItems, setExpandedItems] = useState({});

  // Check Supabase connection status
  useEffect(() => {
    const checkConnection = async () => {
      try {
        // Simple health check - try to get session
        const { error } = await supabase.auth.getSession();
        setConnectionStatus(error ? 'offline' : 'online');
      } catch {
        setConnectionStatus('offline');
      }
    };

    checkConnection();

    // Re-check every 30 seconds
    const interval = setInterval(checkConnection, 30000);

    // Also listen for online/offline browser events
    const handleOnline = () => checkConnection();
    const handleOffline = () => setConnectionStatus('offline');

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      clearInterval(interval);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Role-based navigation configurations
  const navigationConfig = {
    superadmin: [
      {
        label: 'Overview',
        path: '/super-admin/dashboard',
        icon: LayoutDashboard,
        tooltip: 'Platform overview and metrics'
      },
      {
        label: 'All Companies',
        path: '/super-admin/companies',
        icon: Building2,
        tooltip: 'Manage all companies'
      },
      {
        label: 'All Users',
        path: '/super-admin/all-users',
        icon: Users,
        tooltip: 'View and manage all users'
      },
      {
        label: 'Logs',
        path: '/audit-logging-system',
        icon: FileText,
        tooltip: 'System activity and compliance logs'
      },
      {
        label: 'Company Merge',
        path: '/company-merge-simulation',
        icon: GitMerge,
        tooltip: 'Company merge operations'
      },
      {
        label: 'Performance Monitor',
        path: '/performance-health-monitor',
        icon: Activity,
        tooltip: 'System health and performance'
      },
      {
        label: 'Settings',
        path: '/super-admin/settings',
        icon: Settings,
        tooltip: 'Admin settings and preferences'
      }
    ],
    corporate: [
      {
        label: 'Dashboard',
        path: '/corporate/dashboard',
        icon: LayoutDashboard,
        tooltip: 'Company overview and metrics'
      },
      {
        label: 'Quotations',
        path: '/corporate/quotes',
        icon: ScrollText,
        tooltip: 'Create and manage professional quotes'
      },
      {
        label: 'Workforce',
        path: '/workforce-management',
        icon: Users,
        tooltip: 'Manage employees and contractors'
      },
      {
        label: 'Clients & Agencies',
        path: '/corporate/clients-agencies',
        icon: Building,
        tooltip: 'Manage partner agencies and service receivers'
      },
      {
        label: 'Job Management',
        path: '/job-management',
        icon: Briefcase,
        tooltip: 'Create and manage jobs'
      },
      {
        label: 'Reports',
        path: '/corporate/reports',
        icon: ClipboardList,
        tooltip: 'Review submitted work and approve timesheets'
      },
      {
        label: 'Earnings',
        path: '/corporate/earnings',
        icon: DollarSign,
        tooltip: 'Track payments and manage contractor earnings'
      },
      {
        label: 'Templates',
        path: '/corporate/templates',
        icon: FileStack,
        tooltip: 'Create and manage inspection templates'
      },
      {
        label: 'Inspections',
        path: '/corporate/inspections',
        icon: ClipboardCheck,
        tooltip: 'Manage site inspections and schedules'
      },
      {
        label: 'Logs',
        path: '/audit-logging-system',
        icon: FileText,
        tooltip: 'Activity logs and reports'
      },
      {
        label: 'Settings',
        path: '/corporate/settings?tab=profile',
        icon: Settings,
        tooltip: 'Company settings',
        children: [
          { label: 'Company Profile', path: '/corporate/settings?tab=profile', icon: Building2 },
          { label: 'Invoice Settings', path: '/corporate/settings?tab=invoice', icon: FileText },
          { label: 'Job Configuration', path: '/corporate/settings?tab=jobs', icon: Briefcase },
          { label: 'Notifications', path: '/corporate/settings?tab=notifications', icon: Bell },
          { label: 'Permissions', path: '/corporate/settings?tab=permissions', icon: Shield },
          { label: 'Security & Account', path: '/corporate/settings?tab=security', icon: Shield }
        ]
      }
    ],
    contractor: [
      {
        label: 'Dashboard',
        path: '/contractor/dashboard',
        icon: LayoutDashboard,
        tooltip: 'Your overview and stats'
      },
      {
        label: 'Today\'s Jobs',
        path: '/contractor/today-jobs',
        icon: Calendar,
        tooltip: 'Jobs scheduled for today'
      },
      {
        label: 'Upcoming Jobs',
        path: '/contractor/upcoming-jobs',
        icon: Clock,
        tooltip: 'Future scheduled jobs'
      },
      {
        label: 'Earnings',
        path: '/contractor/earnings',
        icon: DollarSign,
        tooltip: 'Track your earnings'
      },
      {
        label: 'Inspections',
        path: '/contractor/inspections',
        icon: ClipboardCheck,
        tooltip: 'View and conduct assigned inspections'
      },
      {
        label: 'Settings',
        path: '/contractor/settings?tab=profile',
        icon: Settings,
        tooltip: 'Your profile and settings',
        children: [
          { label: 'Profile', path: '/contractor/settings?tab=profile', icon: User },
          { label: 'Notifications', path: '/contractor/settings?tab=notifications', icon: Bell },
          { label: 'Job Preferences', path: '/contractor/settings?tab=preferences', icon: Tag },
          { label: 'Security', path: '/contractor/settings?tab=security', icon: Shield }
        ]
      }
    ]
  };

  // Get navigation items based on effective account type (derived from current company role)
  const getNavigationItems = () => {
    return navigationConfig?.[effectiveAccountType] || navigationConfig?.contractor;
  };

  const navigationItems = getNavigationItems();

  // Auto-expand Settings when on settings page, collapse when navigating away
  useEffect(() => {
    if (location.pathname.includes('/settings')) {
      setExpandedItems({ 'Settings': true });
    } else {
      setExpandedItems({});
    }
  }, [location.pathname]);

  const handleNavigation = (path, hasChildren = false) => {
    // Extract base path (without query params) to check if navigating away from settings
    const basePath = path.split('?')[0];
    const isSettingsPath = basePath.includes('/settings');

    // If navigating to a non-settings item, collapse settings
    if (!isSettingsPath) {
      setExpandedItems({});
    } else if (hasChildren) {
      // Clicking settings parent expands it
      setExpandedItems({ 'Settings': true });
    }

    navigate(path);
    close(); // Close sidebar on mobile after navigation
  };

  const isActivePath = (path) => {
    // For paths with query params, check both pathname and search
    const [pathBase, pathSearch] = path.split('?');
    if (pathSearch) {
      return location?.pathname === pathBase && location?.search === `?${pathSearch}`;
    }
    return location?.pathname === path;
  };

  const isSettingsActive = () => {
    return location?.pathname.includes('/settings');
  };

  const getActiveTab = () => {
    return searchParams.get('tab') || 'profile';
  };

  return (
    <>
      {/* Backdrop Overlay - Mobile Only (closes sidebar when clicking outside) */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-80 lg:hidden"
          onClick={close}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-[73px] bottom-0 bg-background border-r border-border w-60
          transition-transform duration-300 ease-in-out z-90 safe-area-top
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:translate-x-0
        `}
      >
        {/* Close Button - Mobile Only */}
        <button
          onClick={close}
          className="absolute top-4 right-4 p-1 text-text-secondary hover:text-text-primary lg:hidden"
          aria-label="Close sidebar"
        >
          <X size={20} />
        </button>

        {/* Navigation */}
        <nav className="flex-1 p-4 pt-12 lg:pt-4 overflow-y-auto">
          <div className="space-y-2">
            {navigationItems?.map((item) => {
              const hasChildren = item?.children?.length > 0;
              const isExpanded = expandedItems[item?.label];
              const isParentActive = hasChildren
                ? isSettingsActive()
                : isActivePath(item?.path);
              const IconComponent = item?.icon;

              return (
                <div key={item?.path} className="relative group">
                  {/* Parent Navigation Item */}
                  <button
                    onClick={() => handleNavigation(item?.path, hasChildren)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all duration-200 ${
                      isParentActive
                        ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/20'
                        : 'text-text-secondary hover:text-text-primary hover:bg-surface'
                    }`}
                    title={item?.tooltip}
                  >
                    <div className="flex items-center space-x-3">
                      <IconComponent
                        size={20}
                        color={isParentActive ? 'white' : 'currentColor'}
                      />
                      <span className="font-medium text-sm">{item?.label}</span>
                    </div>

                    {hasChildren ? (
                      <ChevronDown
                        size={16}
                        className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    ) : isParentActive ? (
                      <div className="w-2 h-2 bg-white rounded-full" />
                    ) : null}
                  </button>

                  {/* Child Navigation Items (Settings Sub-tabs) */}
                  {hasChildren && isExpanded && (
                    <div className="mt-1 ml-4 pl-4 border-l border-border space-y-1">
                      {item.children.map((child) => {
                        const childTab = child.path.split('tab=')[1];
                        const isChildActive = isSettingsActive() && getActiveTab() === childTab;
                        const ChildIcon = child.icon;

                        return (
                          <button
                            key={child.path}
                            onClick={() => handleNavigation(child.path)}
                            className={`w-full flex items-center space-x-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                              isChildActive
                                ? 'text-orange-400 bg-orange-500/10'
                                : 'text-text-secondary hover:text-text-primary hover:bg-surface'
                            }`}
                          >
                            <ChildIcon size={16} />
                            <span>{child.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </nav>

        {/* Sidebar Footer - Connection Status */}
        <div className="p-3 sm:p-4 border-t border-border safe-area-bottom">
          <div className="flex items-center space-x-3 p-3 bg-surface rounded-lg">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              connectionStatus === 'online' ? 'bg-green-500' :
              connectionStatus === 'offline' ? 'bg-red-500' : 'bg-yellow-500'
            }`}>
              {connectionStatus === 'online' && <Shield size={16} color="white" />}
              {connectionStatus === 'offline' && <WifiOff size={16} color="white" />}
              {connectionStatus === 'checking' && <Loader2 size={16} color="white" className="animate-spin" />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-text-primary">
                {connectionStatus === 'online' && 'System Online'}
                {connectionStatus === 'offline' && 'Connection Lost'}
                {connectionStatus === 'checking' && 'Connecting...'}
              </p>
              <p className="text-xs text-text-secondary">
                {connectionStatus === 'online' && 'All services operational'}
                {connectionStatus === 'offline' && 'Check your internet connection'}
                {connectionStatus === 'checking' && 'Verifying connection'}
              </p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;